import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { PrivateRoute } from './components/PrivateRoute';
import { HomePage } from './HomePage';
import { LoginPage, ResetPassword, PasswordChanged } from './pages/auth';
import { PageNotFound } from './pages/errors';
import Wholesalertools from './components/Wholesaler/Tools';
import Admintools from './components/Admin/Tools';
import Wholesalerpayment from './components/Wholesaler/Payment';
import DistributorTools from './components/Distributor/Tools';
import WholesalerReports from './components/Wholesaler/Reports';
import WholesalerCanvasDesigner from './components/Wholesaler/CanvasDesigner';
import Analytics from './components/Wholesaler/Analytics';
import WhGeographic from './components/Wholesaler/Geographic';
import WhPharmacies from './components/Wholesaler/Pharmacies';
import Analyticsa from './components/Admin/Analytics';
import AdGeographic from './components/Admin/Geographic';
import AdPharmacies from './components/Admin/Pharmacies';
import AdPublicAPI from './components/Admin/PublicAPI';
import AdminReports from './components/Admin/Reports';
import AdvanceFilter from './components/AdvanceFilter';
import { Helmet, HelmetProvider } from "react-helmet-async";
import CompanyOverview from './components/Admin/CompanyOverview';
import { userService } from './_services';
import SystemPopupOrchestrator from './components/SystemPopupOrchestrator';
import { SessionEndedProvider } from './context/SessionEndedContext';
import { useSessionActivityGuard } from './hooks/useSessionActivityGuard';
import SessionEndedHost from './components/SessionEndedHost';

// Component to handle page title updates on route changes
function TitleManager() {
    const location = useLocation();
    
    const pageTitle = useMemo(() => {
        const pathname = location.pathname;
        const isLoggedIn = userService.Check_LoggedIn_Status() !== 'Login';
        
        const titleMap = {
            '/filter': 'Advanced Filters',
            '/admin/report': 'Admin - Reports',
            '/wholesaler/report': 'Wholesaler - Reports',
            '/admin/tools': 'Admin - Tools',
            '/wholesaler/tools': 'Wholesaler - Tools',
            '/wholesaler/payment': 'Wholesaler - Payments',
            '/distributor/tools': 'Distributor - Tools',
            '/dashboard': 'Dashboard',
            '/wholesaler/analytics/signups': 'Wholesaler - Analytics',
            '/wholesaler/analytics/geographic': 'Wholesaler - Geographic',
            '/wholesaler/analytics/pharmacies': 'Wholesaler - Pharmacies',
            '/admin/analytics/signups': 'Admin - Analytics',
            '/admin/analytics/geographic': 'Admin - Geographic',
            '/admin/analytics/pharmacies': 'Admin - Pharmacies',
            '/admin/analytics/publicapi': 'Admin - Public API',
            '/login': 'Login',
            '/resetPassword': 'Reset Password',
            '/passwordChanged': 'Password Changed',
            '/admin/companyOverview': 'Admin - Company Overview',
            '/wholesaler/whitelabel-setup': 'Whitelabel Setup',
            '/pageNotFound': 'Page Not Found',
        };

        // Handle paths with or without trailing slash
        const normalizedPath = pathname.endsWith('/') && pathname !== '/' 
            ? pathname.slice(0, -1) 
            : pathname;
        
        if (titleMap[normalizedPath]) {
            return titleMap[normalizedPath];
        }
        
        if (normalizedPath === '/' || normalizedPath === '') {
            return isLoggedIn ? 'Dashboard' : 'Login';
        }
        
        return 'Page Not Found';
    }, [location.pathname]);

    return (
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=yes" />
        </Helmet>
    );
}

function App() {
    // Session expiry + activity-based extension.
    // Polls CheckSessionActivity and extends by 24 h (10 min on localhost) when
    // the user is active within the last warning window before logout.
    useSessionActivityGuard();

    // Set favicon based on domain
    useEffect(() => {
        try {
            const favicon = document.getElementById("favicon");
            if (!favicon) return;
            
            const hostname = window.location.hostname;
            const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
            
            if (hostname.split('.')[1] === 'glichealth' && 
                window.location.host.toLowerCase() !== "tempfilter.23byt.es") {
                favicon.href = "/glicrx_favicon.ico";
            } else if (domain === "rxhacker.com" || 
                       domain === "localhost:5464" || 
                       window.location.host.toLowerCase() === "tempfilter.23byt.es") {
                favicon.href = "/Rxhacker_favicon_new.ico";
            } else {
                favicon.href = "/Rxhacker_favicon_new.ico";
            }
        } catch (err) {
        }
    }, []);

    return (
        <HelmetProvider>
            <SessionEndedProvider>
                <Router getUserConfirmation={(message, callback) => {
                    // When a mandatory popup is active, block navigation silently (no browser confirm)
                    if (message === '__MANDATORY_POPUP_BLOCK__') { callback(false); return; }
                    callback(window.confirm(message));
                }}>
                    <TitleManager />
                    <Switch>
                    <PrivateRoute exact path="/" component={HomePage} />
                    <Route exact path="/login" component={LoginPage} />
                    <Route exact path="/dashboard" component={HomePage} />
                    <Route exact path="/wholesaler/tools" component={Wholesalertools} />
                    <Route exact path="/wholesaler/payment" component={Wholesalerpayment} />
                    <Route exact path="/wholesaler/analytics/signups" component={Analytics} />
                    <Route exact path="/wholesaler/analytics/geographic" component={WhGeographic} />
                    <Route exact path="/wholesaler/analytics/pharmacies" component={WhPharmacies} />
                    <Route exact path="/admin/analytics/signups" component={Analyticsa} />
                    <Route exact path="/admin/analytics/geographic" component={AdGeographic} />
                    <Route exact path="/admin/analytics/pharmacies" component={AdPharmacies} />
                    <Route exact path="/admin/analytics/publicapi" component={AdPublicAPI} />
                    <Route exact path="/admin/tools" component={Admintools} />
                    <Route exact path="/distributor/tools" component={DistributorTools} />
                    <Route exact path="/wholesaler/report" component={WholesalerReports} />
                    <Route exact path="/admin/report" component={AdminReports} />
                    <Route exact path="/resetPassword" component={ResetPassword} />
                    <Route exact path="/passwordChanged" component={PasswordChanged} />
                    <Route exact path="/filter" component={AdvanceFilter} />
                    <Route exact path="/pageNotFound" component={PageNotFound} />
                    <Route exact path="/admin/companyOverview" component={CompanyOverview} />
                    <Route exact path="/wholesaler/whitelabel-setup" component={WholesalerCanvasDesigner} />
                    <Redirect from="/wholesaler/canvas-designer" to="/wholesaler/whitelabel-setup" />
                    <Route path="*" component={PageNotFound} />
                </Switch>
                <SystemPopupOrchestrator />
                <SessionEndedHost />
            </Router>
            </SessionEndedProvider>
        </HelmetProvider>
    );
}

export default App;
